import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { exportXlsx } from './exportXlsx';

const todayStr = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString().slice(0, 10); };
const fmt = (d: string) => new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtDay = (d: string) => new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

export const AdminOrdersByUserScreen: React.FC = () => {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [groups, setGroups] = useState<any[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const data = await adminApi.ordersByUser({ from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` });
    setGroups(data);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const markGroup = async (g: any) => {
    const unpaid = g.orders.filter((o: any) => !o.externallyPaid);
    if (unpaid.length === 0) return;
    const who = g.userName || g.externalRef || 'this user';
    if (!window.confirm(`Mark ${unpaid.length} delivered order(s) for ${who} as externally paid? This cannot be undone.`)) return;
    setMsg('');
    try {
      const r = await adminApi.markExternalPaid(unpaid.map((o: any) => o.id));
      setMsg(`Marked ${r.markedCount} order(s) externally paid · R ${Number(r.totalAmount).toFixed(2)}.`);
      await load();
    } catch (e: any) {
      setMsg(e?.response?.data?.message || 'Failed to mark externally paid');
    }
  };

  const exportRows = () => {
    const flat: Record<string, any>[] = [];
    for (const g of groups) {
      for (const o of g.orders) {
        flat.push({
          User: g.userName || (g.externalRef ? `Unknown ref: ${g.externalRef}` : ''),
          Phone: g.userPhone || '',
          'Wallet Ref': g.externalRef || '',
          'Order #': o.orderNumber,
          Status: o.status,
          Amount: Number(o.totalAmount ?? 0),
          'Order Date': fmt(o.createdAt),
          'Externally Paid': o.externallyPaid ? 'Yes' : 'No',
          'Paid Date': o.externallyPaid && o.externallyPaidAt ? fmtDay(o.externallyPaidAt) : '',
          'Paid Amount': o.externallyPaid ? Number(o.externallyPaidAmount ?? 0) : '',
          'Paid By': o.externallyPaidBy || '',
        });
      }
    }
    exportXlsx(`orders-by-user_${from}_${to}.xlsx`, 'Orders by User', flat);
  };

  const hasOrders = groups.some((g) => g.orders.length > 0);

  return (
    <div>
      <div className="admin-filters">
        <label>From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="admin-btn allocate" onClick={exportRows} disabled={!hasOrders}>
          <i className="fas fa-file-excel"></i> Export to Excel
        </button>
      </div>
      <p className="admin-muted">Showing delivered orders only.</p>
      {msg && <p className="admin-muted">{msg}</p>}
      {groups.length === 0 && <p className="admin-muted">No delivered orders in this range.</p>}
      {groups.map((g, i) => {
        const key = g.externalRef || `none-${i}`;
        const paidCount = g.orders.filter((o: any) => o.externallyPaid).length;
        const allPaid = paidCount === g.orders.length;
        const paidTotal = g.orders.reduce((s: number, o: any) => s + (o.externallyPaid ? (Number(o.externallyPaidAmount) || 0) : 0), 0);
        return (
          <div className="admin-group" key={key}>
            <div className="admin-group-header">
              <span onClick={() => setOpen({ ...open, [key]: !open[key] })} style={{ cursor: 'pointer', flex: 1 }}>
                <strong>{g.userName || (g.externalRef ? `Unknown wallet ref: ${g.externalRef}` : 'No external ref')}</strong>
                {g.userPhone && <span className="admin-muted"> · {g.userPhone}</span>}
                <span className="admin-muted"> · {g.orderCount} delivered · R {Number(g.totalAmount || 0).toFixed(2)}</span>
                <span className="ext-chip">{paidCount}/{g.orderCount} externally paid{paidTotal > 0 ? ` · R ${paidTotal.toFixed(2)}` : ''}</span>
                <i className={`fas fa-chevron-${open[key] ? 'up' : 'down'}`} style={{ marginLeft: '0.5rem' }}></i>
              </span>
              <button
                className="admin-btn allocate"
                disabled={allPaid}
                onClick={() => markGroup(g)}
              >
                {allPaid ? 'All externally paid' : 'Mark all as externally paid'}
              </button>
            </div>
            {open[key] && (
              <table className="admin-table">
                <thead><tr><th>Order #</th><th>Status</th><th>Amount</th><th>Date</th><th>Externally Paid</th><th>Paid Amount</th></tr></thead>
                <tbody>
                  {g.orders.map((o: any) => (
                    <tr key={o.id}>
                      <td>{o.orderNumber}</td>
                      <td>{o.status}</td>
                      <td>R {Number(o.totalAmount ?? 0).toFixed(2)}</td>
                      <td>{fmt(o.createdAt)}</td>
                      <td title={o.externallyPaidBy || ''}>
                        {o.externallyPaid
                          ? <span className="ext-paid"><i className="fas fa-check-circle"></i> {o.externallyPaidAt ? fmtDay(o.externallyPaidAt) : 'yes'}</span>
                          : <span className="admin-muted">-</span>}
                      </td>
                      <td>{o.externallyPaid ? `R ${Number(o.externallyPaidAmount ?? 0).toFixed(2)}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};
